'use client'

import { useState } from 'react'
import Image from 'next/image'
import { Play } from 'lucide-react'
import {
  youtubeEmbedUrl,
  youtubeThumbnailUrl,
  type YouTubeVideo,
} from '@/data/youtube-videos'

/**
 * Click-to-play YouTube embed. Until the visitor presses play, the page shows
 * the video's thumbnail through our own image optimizer, so a page view never
 * contacts YouTube. The consent manager governs analytics only; this is what
 * keeps an embed from calling a third party before the visitor asks for it.
 *
 * Fills its parent: give the parent `relative aspect-video`.
 */
export default function YouTubeFacade({
  video,
  sizes,
}: {
  video: Pick<YouTubeVideo, 'id' | 'title'>
  sizes: string
}) {
  const [playing, setPlaying] = useState(false)

  if (playing) {
    return (
      <iframe
        className='absolute inset-0 h-full w-full'
        src={`${youtubeEmbedUrl(video.id)}?autoplay=1`}
        title={video.title}
        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
        referrerPolicy='strict-origin-when-cross-origin'
        allowFullScreen
      />
    )
  }

  return (
    <button
      type='button'
      onClick={() => setPlaying(true)}
      aria-label={`Play video: ${video.title}`}
      className='group absolute inset-0 h-full w-full cursor-pointer'
    >
      <Image
        src={youtubeThumbnailUrl(video.id)}
        alt=''
        fill
        sizes={sizes}
        className='object-cover'
      />
      <span
        aria-hidden='true'
        className='absolute inset-0 flex items-center justify-center'
      >
        <span className='flex h-16 w-16 items-center justify-center bg-[var(--cta-fill)] text-white transition-transform duration-200 group-hover:scale-110 motion-reduce:transition-none motion-reduce:group-hover:scale-100'>
          <Play size={24} fill='currentColor' />
        </span>
      </span>
    </button>
  )
}
